import axios, { AxiosInstance } from 'axios';
import {
  AppListResponse,
  AppResponse,
  DeviceListResponse,
  AnalyticsResponse,
  AttestationAnalyticsResponse,
  DeviceActivationAnalyticsResponse,
  SystemStatsResponse,
  AppRegistrationRequest,
  AppUpdateRequest,
  DeviceActionRequest,
  AnalyticsRequest,
  DeviceActivationAnalyticsRequest,
  // Vendor/Device Management Imports
  VendorResponse,
  VendorListResponse,
  VendorDeviceResponse,
  VendorDeviceListResponse,
  VendorDevicePermissionResponse,
  VendorDevicePermissionListResponse,
  VendorAnalyticsResponse,
  CreateVendorRequest,
  UpdateVendorRequest,
  CreateVendorDeviceRequest,
  UpdateVendorDeviceRequest,
  GrantVendorDevicePermissionRequest,
  VendorAnalyticsRequest,
  // Unique Device Analytics
  UniqueDeviceAnalyticsRequest,
  UniqueDeviceAnalyticsResponse
} from '../types/api';

class AdminApiService {
  private api: AxiosInstance;
  private adminKey: string | null;
  private adminUser: string | null;

  constructor() {
    // No default admin credentials - must be provided via login
    this.adminKey = null;
    this.adminUser = null;
    
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Debug logging to verify correct API URL
    console.log('🔗 Admin API Service initialized with baseURL:', process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1');

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Clear credentials on auth failure
          this.clearCredentials();
          throw new Error('Authentication failed. Please check your admin credentials.');
        }
        if (error.response?.data) {
          throw new Error(error.response.data.message || 'API request failed');
        }
        throw new Error(error.message || 'Network error');
      }
    );
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!(this.adminKey);
  }

  // Update credentials (for login functionality)
  setCredentials(adminKey: string, adminUser: string) {
    this.adminKey = adminKey;
    this.adminUser = adminUser;
    this.api.defaults.headers['Authorization'] = `Bearer ${adminKey}`;
  }

  // Clear credentials (for logout)
  clearCredentials() {
    this.adminKey = null;
    this.adminUser = null;
    delete this.api.defaults.headers['Authorization'];
  }

  // Validate credentials by testing an admin endpoint
  async validateCredentials(adminKey: string, adminUser: string): Promise<boolean> {
    try {
      // Temporarily set credentials for validation
      const tempApi = axios.create({
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1',
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey}`,
        },
      });

      await tempApi.get('/admin/health');
      return true;
    } catch (error) {
      return false;
    }
  }

  // Health check
  async checkHealth(): Promise<{ success: boolean; message: string }> {
    const response = await this.api.get('/admin/health');
    return response.data;
  }

  // ========================================
  // Application Management
  // ========================================

  async getApps(params?: {
    environment?: string;
    active_only?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<AppListResponse> {
    const response = await this.api.get('/admin/apps', { params });
    return response.data;
  }

  async registerApp(request: AppRegistrationRequest): Promise<AppResponse> {
    const response = await this.api.post('/admin/apps', request);
    return response.data;
  }

  async updateApp(appId: string, request: AppUpdateRequest): Promise<AppResponse> {
    const response = await this.api.put(`/admin/apps/${appId}`, request);
    return response.data;
  }

  // ========================================
  // Vendor Management
  // ========================================

  async getVendors(params?: {
    page?: number;
    page_size?: number;
    active_only?: boolean;
  }): Promise<VendorListResponse> {
    const response = await this.api.get('/admin/vendors', { params });
    return response.data;
  }

  async getVendor(vendorId: number): Promise<VendorResponse> {
    const response = await this.api.get(`/admin/vendors/${vendorId}`);
    return response.data;
  }

  async createVendor(request: CreateVendorRequest): Promise<VendorResponse> {
    const response = await this.api.post('/admin/vendors', request);
    return response.data;
  }

  async updateVendor(vendorId: number, request: UpdateVendorRequest): Promise<VendorResponse> {
    const response = await this.api.put(`/admin/vendors/${vendorId}`, request);
    return response.data;
  }

  // ========================================
  // Vendor Device Management
  // ========================================

  async getVendorDevices(vendorId: number, params?: {
    page?: number;
    page_size?: number;
    active_only?: boolean;
  }): Promise<VendorDeviceListResponse> {
    const response = await this.api.get(`/admin/vendors/${vendorId}/devices`, { params });
    return response.data;
  }

  async createVendorDevice(vendorId: number, request: CreateVendorDeviceRequest): Promise<VendorDeviceResponse> {
    const response = await this.api.post(`/admin/vendors/${vendorId}/devices`, request);
    return response.data;
  }

  async updateVendorDevice(
    vendorId: number, 
    deviceId: number, 
    request: UpdateVendorDeviceRequest
  ): Promise<VendorDeviceResponse> {
    const response = await this.api.put(`/admin/vendors/${vendorId}/devices/${deviceId}`, request);
    return response.data;
  }

  // ========================================
  // Vendor Device Permissions
  // ========================================

  async getAppPermissions(appId: string, params?: {
    page?: number;
    page_size?: number;
    active_only?: boolean;
  }): Promise<VendorDevicePermissionListResponse> {
    const response = await this.api.get(`/admin/apps/${appId}/permissions`, { params });
    return response.data;
  }

  async grantVendorDevicePermission(
    appId: string, 
    request: GrantVendorDevicePermissionRequest
  ): Promise<VendorDevicePermissionResponse> {
    const response = await this.api.post(`/admin/apps/${appId}/permissions`, request);
    return response.data;
  }

  async revokeVendorDevicePermission(appId: string, permissionId: string): Promise<void> {
    await this.api.delete(`/admin/apps/${appId}/permissions/${permissionId}`);
  }

  // ========================================
  // Vendor Analytics
  // ========================================

  async getVendorAnalytics(request: VendorAnalyticsRequest): Promise<VendorAnalyticsResponse> {
    const response = await this.api.post('/admin/analytics/vendor', request);
    return response.data;
  }

  // ========================================
  // Device Management
  // ========================================

  async getDevices(params?: {
    environment?: string;
    blocked_only?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<DeviceListResponse> {
    const response = await this.api.get('/admin/devices', { params });
    return response.data;
  }

  async deviceAction(deviceId: string, request: DeviceActionRequest): Promise<{
    device_id: string;
    action: string;
    success: boolean;
    message: string;
    timestamp: string;
  }> {
    const response = await this.api.post(`/admin/devices/${deviceId}/actions`, request);
    return response.data;
  }

  // ========================================
  // Analytics
  // ========================================

  async getAnalytics(request: AnalyticsRequest): Promise<AnalyticsResponse> {
    const response = await this.api.post('/admin/analytics', request);
    return response.data;
  }

  async getAttestationAnalytics(request: {
    start_date?: string;
    end_date?: string;
    security_level?: string;
    verified_only?: boolean;
  }): Promise<AttestationAnalyticsResponse> {
    const response = await this.api.post('/admin/analytics/attestation', request);
    return response.data;
  }

  async getDeviceActivationAnalytics(request: DeviceActivationAnalyticsRequest): Promise<DeviceActivationAnalyticsResponse> {
    const response = await this.api.post('/admin/analytics/activations', request);
    return response.data;
  }

  async getUniqueDeviceAnalytics(request: UniqueDeviceAnalyticsRequest): Promise<UniqueDeviceAnalyticsResponse> {
    const response = await this.api.post('/admin/analytics/unique-devices', request);
    return response.data;
  }

  // ========================================
  // System Monitoring
  // ========================================

  async getSystemStats(): Promise<SystemStatsResponse> {
    const response = await this.api.get('/admin/system/stats');
    return response.data;
  }

  // ========================================
  // Advanced Analytics (Phase 6)
  // ========================================

  async getAnalyticsHealth(): Promise<{ status: string; health: any; timestamp: string }> {
    const response = await this.api.get('/analytics/health');
    return response.data;
  }

  async getSecurityInsights(): Promise<{
    status: string;
    insights: any;
    ml_models_active: boolean;
    timestamp: string;
  }> {
    const response = await this.api.get('/analytics/security/insights');
    return response.data;
  }

  async getAnomalyAlerts(params?: {
    hours?: number;
    severity?: string;
    alert_type?: string;
  }): Promise<{
    status: string;
    total_alerts: number;
    filters: any;
    alerts: any[];
  }> {
    const response = await this.api.get('/analytics/anomalies/alerts', { params });
    return response.data;
  }

  async analyzeDeviceBehavior(deviceId: string): Promise<{
    status: string;
    device_id: string;
    analysis_timestamp: string;
    anomaly_detected: boolean;
    alert?: any;
    message?: string;
  }> {
    const response = await this.api.post(`/analytics/anomalies/analyze-device?device_id=${deviceId}`);
    return response.data;
  }

  async detectPatterns(): Promise<{
    status: string;
    analysis_timestamp: string;
    patterns_detected: number;
    alerts: any[];
  }> {
    const response = await this.api.post('/analytics/patterns/detect');
    return response.data;
  }

  async getMLModelStatus(): Promise<{
    status: string;
    ml_status: any;
    timestamp: string;
  }> {
    const response = await this.api.get('/analytics/ml/model-status');
    return response.data;
  }

  async initializeMLModels(): Promise<{
    status: string;
    message: string;
    models_active: any;
    timestamp: string;
  }> {
    const response = await this.api.post('/analytics/ml/initialize');
    return response.data;
  }

  async getAnalyticsDashboardSummary(): Promise<{
    status: string;
    summary: any;
    timestamp: string;
  }> {
    const response = await this.api.get('/analytics/dashboard/summary');
    return response.data;
  }

  async startContinuousMonitoring(): Promise<{
    status: string;
    message: string;
    models_ready: boolean;
    timestamp: string;
  }> {
    const response = await this.api.post('/analytics/monitoring/start');
    return response.data;
  }

  // ========================================
  // Settings Management
  // ========================================

  async getSettings(): Promise<{ settings: any }> {
    const response = await this.api.get('/admin/settings');
    return response.data;
  }

  async updateSettings(settings: any): Promise<{
    success: boolean;
    message: string;
    updated_settings: any;
  }> {
    const response = await this.api.put('/admin/settings', settings);
    return response.data;
  }

  // ========================================
  // Utility Methods
  // ========================================

  // Get quick dashboard data
  async getDashboardData(): Promise<{
    systemStats: SystemStatsResponse;
    recentAnalytics: AnalyticsResponse;
    recentActivations: DeviceActivationAnalyticsResponse;
  }> {
    const [systemStats, recentAnalytics, recentActivations] = await Promise.all([
      this.getSystemStats(),
      this.getAnalytics({ group_by: 'day' }),
      this.getDeviceActivationAnalytics({ group_by: 'day' })
    ]);

    return {
      systemStats,
      recentAnalytics,
      recentActivations
    };
  }

  // Get quick vendor dashboard data
  async getVendorDashboardData(): Promise<{
    vendors: VendorListResponse;
    topVendorAnalytics?: VendorAnalyticsResponse[];
  }> {
    const vendors = await this.getVendors({ page: 1, page_size: 10, active_only: true });
    
    // Get analytics for top vendors
    const topVendorAnalytics: VendorAnalyticsResponse[] = [];
    for (const vendor of vendors.vendors.slice(0, 3)) {
      try {
        const analytics = await this.getVendorAnalytics({
          vendor_id: vendor.vendor_id,
          ...this.getDateRange('week')
        });
        topVendorAnalytics.push(analytics);
      } catch (error) {
        // Skip if analytics fail for a vendor
        console.warn(`Failed to get analytics for vendor ${vendor.vendor_id}:`, error);
      }
    }

    return {
      vendors,
      topVendorAnalytics
    };
  }

  // Format date for API requests
  formatDate(date: Date): string {
    return date.toISOString();
  }

  // Get date range for common periods
  getDateRange(period: 'today' | 'week' | 'month' | 'quarter'): { start_date: string; end_date: string } {
    const now = new Date();
    const end_date = this.formatDate(now);
    let start_date: string;

    switch (period) {
      case 'today':
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        start_date = this.formatDate(today);
        break;
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        start_date = this.formatDate(weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(now);
        monthAgo.setMonth(now.getMonth() - 1);
        start_date = this.formatDate(monthAgo);
        break;
      case 'quarter':
        const quarterAgo = new Date(now);
        quarterAgo.setMonth(now.getMonth() - 3);
        start_date = this.formatDate(quarterAgo);
        break;
      default:
        start_date = this.formatDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
    }

    return { start_date, end_date };
  }
}

// Export singleton instance
export const adminApi = new AdminApiService();
export default adminApi; 